import type { AppState } from '../types/app'

// whereAt remembers which screen she was on. On an iPhone home-screen app,
// opening her booking page (or Stripe, or WhatsApp) leaves the app, and iOS
// often reloads it on the way back — which used to drop her on Today.

const KEY = 'harmovi.whereAt'
// Long enough to check her page and come back; short enough that opening the
// app tomorrow still starts on Today.
const FRESH_MS = 30 * 60 * 1000

type WhereAt = Pick<AppState, 'activeTab' | 'menuOpen' | 'settingsPanel' | 'openClientId'>

export function rememberWhereAt(state: WhereAt, now = Date.now()): void {
  const { activeTab, menuOpen, settingsPanel, openClientId } = state
  try {
    localStorage.setItem(KEY, JSON.stringify({ activeTab, menuOpen, settingsPanel, openClientId, at: now }))
  } catch {
    // Private mode or storage full: she just starts on Today.
  }
}

export function recallWhereAt(now = Date.now()): Partial<WhereAt> {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? 'null')
    if (!saved || typeof saved.at !== 'number' || now - saved.at > FRESH_MS) {
      return {}
    }
    return {
      activeTab: saved.activeTab,
      menuOpen: saved.menuOpen === true,
      settingsPanel: saved.settingsPanel ?? null,
      openClientId: saved.openClientId ?? null,
    }
  } catch {
    return {}
  }
}

// forgetOpenPanel drops a remembered Settings page (and the menu) but keeps
// the tab: when a page failed to load, reopening the app must not land on it
// again.
// It returns what was remembered, so a Retry that reloads the page can put it
// back (restoreWhereAt) and land on the same page again.
export function forgetOpenPanel(): string | null {
  try {
    const raw = localStorage.getItem(KEY)
    const saved = JSON.parse(raw ?? 'null')
    if (!saved || typeof saved !== 'object') return null
    localStorage.setItem(KEY, JSON.stringify({ ...saved, menuOpen: false, settingsPanel: null }))
    return raw
  } catch {
    return null
  }
}

export function restoreWhereAt(raw: string | null): void {
  if (!raw) return
  try {
    localStorage.setItem(KEY, raw)
  } catch {
    // She starts on the tab instead.
  }
}

export function forgetWhereAt(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // Nothing to forget.
  }
}
